import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject, ReplaySubject } from 'rxjs';

import { PreloginComponent } from '../auth/prelogin/prelogin.component';
import { ErrorComponent } from '../error/error.component';
import { RightPlaceComponent } from './right-place/right-place.component';
import { ImagePreviewComponent } from './image-preview/image-preview.component';
import { ApplyToAllComponent } from './apply-to-all/apply-to-all.component';
import { OrderSummaryComponent } from './order-summary/order-summary.component';
import { DeleteOrderComponent } from './delete-order/delete-order.component';
import { DeleteUserComponent } from './delete-user/delete-user.component';
import { AddPointsComponent } from './add-points/add-points.component';
import { CloseBranchComponent } from './close-branch/close-branch.component';
import { ResizeComponent } from './resize/resize.component';
import { PhoneComponent } from './phone/phone.component';
import { CopyScanComponent } from './scan-copy/scan-copy.component';
import { FixProductsComponent } from './fix-products/fix-products.component';
import { PropertyExplainComponent } from './property-explain/property-explain.component';
import { BorderExplainComponent } from './border-explain/border-explain.component';
import { PrinterNumberComponent } from './printer-number/printer-number.component';
import { SuCloseBranchComponent } from './su-close-branch/su-close-branch.component';
import { SuEditUserComponent } from './su-edit-user/su-edit-user.component';
import { SuFullInventoryComponent } from './su-full-inventory/su-full-inventory.component';
import { SuNewOrderSupplierComponent } from './su-new-order-supplier/su-new-order-supplier.component';
import { SuAllScreensComponent } from './su-all-screens/su-all-screens.component';
import { ProcessingFileComponent } from './processing-file/processing-file.component';
import { LanguageChangeComponent } from './language-change/language-change.component';
import { SuMessageToCustomerComponent } from './su-message-to-customer/su-message-to-customer.component';
import { MessageToCustomerViewComponent } from './message-to-customer-view/message-to-customer-view.component';
import { SuEditLockCodeComponent } from './su-edit-lock-code/su-edit-lock-code.component';
import { TransferFilesToUserComponent } from './transfer-files-to-user/transfer-files-to-user.component';
import { PhPrintExplanationComponent, PhPrintExplanationMode } from './ph-3d-print-explanation/ph-3d-print-explanation.component';
import { FileIssuesComponent } from './file-issues/file-issues.component';
import { SuHelpersListComponent } from './su-helpers-list/su-helpers-list.component';
import { CreatePriceQuoteComponent } from './create-price-quote/create-price-quote.component';
import { CreditOrderComponent, CreditOrderDialogData } from './credit-order/credit-order.component';
import { PrintNodeStatusDialogComponent } from './printnode-status-dialog/printnode-status-dialog.component';
import { BillingErrorsDialogComponent, BillingErrorsDialogData } from './billing-errors-dialog/billing-errors-dialog.component';
import { PaymentRequestDialogComponent } from './payment-request-dialog/payment-request-dialog.component';
import {
  ResendPartialFilesDialogComponent,
  ResendPartialFilesDialogData,
} from './resend-partial-files/resend-partial-files-dialog.component';
import { InkGradientDialogComponent } from './ink-gradient/ink-gradient-dialog.component';
import {
  WallContactDialogComponent,
  WallContactDialogData,
} from './wall-contact-dialog/wall-contact-dialog.component';
import { parsePrintNodeStatusForDialog } from '../other-pages/my-orders/orders-service';
import { wallScrollParent } from '../wall/wall-api.service';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private dialogLoginRef: MatDialogRef<PreloginComponent> | null = null;
  private dialogErrorRef: MatDialogRef<ErrorComponent> | null = null;
  private dialogRightPlaceRef: MatDialogRef<RightPlaceComponent> | null = null;
  private dialogImagePreviewRef: MatDialogRef<ImagePreviewComponent> | null = null;
  private dialogApplyToAllRef: MatDialogRef<ApplyToAllComponent> | null = null;
  private dialogResizeRef: MatDialogRef<ResizeComponent> | null = null;
  private dialogOrderSummaryRef: MatDialogRef<OrderSummaryComponent> | null = null;
  private dialogDeleteOrderRef: MatDialogRef<DeleteOrderComponent> | null = null;
  private dialogDeleteUserRef: MatDialogRef<DeleteUserComponent> | null = null;
  private dialogAddPointsRef: MatDialogRef<AddPointsComponent> | null = null;
  private dialogCloseBranchRef: MatDialogRef<CloseBranchComponent> | null = null;
  private closeResizeDialogSource = new Subject<void>();
  closeResizeDialog$ = this.closeResizeDialogSource.asObservable();
  private dialogPhoneRef: MatDialogRef<PhoneComponent> | null = null;
  private dialogCopyScanRef: MatDialogRef<CopyScanComponent> | null = null;
  private dialogFixProductsRef: MatDialogRef<FixProductsComponent> | null = null;
  private dialogPropertyExplainRef: MatDialogRef<PropertyExplainComponent> | null = null;
  private dialogBorderExplainRef: MatDialogRef<BorderExplainComponent> | null = null;
  private dialogPrinterNumberRef: MatDialogRef<PrinterNumberComponent> | null = null;
  private dialogSuCloseBranchRef: MatDialogRef<SuCloseBranchComponent> | null = null;
  private dialogSuEditUserRef: MatDialogRef<SuEditUserComponent> | null = null;
  private dialogSuFullInventoryRef: MatDialogRef<SuFullInventoryComponent> | null = null;
  private dialogSuNewOrderSupplierRef: MatDialogRef<SuNewOrderSupplierComponent> | null = null;
  private dialogSuAllScreensRef: MatDialogRef<SuAllScreensComponent> | null = null;
  private dialogProcessingFileRef: MatDialogRef<ProcessingFileComponent> | null = null;
  private dialogLanguageChangeRef: MatDialogRef<LanguageChangeComponent> | null = null;
  private dialogSuMessageToCustomerRef: MatDialogRef<SuMessageToCustomerComponent> | null = null;
  private dialogMessageToCustomerViewRef: MatDialogRef<MessageToCustomerViewComponent> | null = null;
  private dialogSuEditLockCodeRef: MatDialogRef<SuEditLockCodeComponent> | null = null;
  private dialogTransferFilesToUserRef: MatDialogRef<TransferFilesToUserComponent> | null = null;
  private dialogPhPrintExplanationRef: MatDialogRef<PhPrintExplanationComponent> | null = null;
  private dialogFileIssuesRef: MatDialogRef<FileIssuesComponent> | null = null;
  private dialogSuHelpersListRef: MatDialogRef<SuHelpersListComponent> | null = null;
  private dialogCreatePriceQuoteRef: MatDialogRef<CreatePriceQuoteComponent> | null = null;
  private dialogCreditOrderRef: MatDialogRef<CreditOrderComponent> | null = null;
  private dialogPaymentRequestRef: MatDialogRef<PaymentRequestDialogComponent> | null = null;
  private dialogResendPartialFilesRef: MatDialogRef<ResendPartialFilesDialogComponent> | null = null;
  private dialogWallContactRef: MatDialogRef<WallContactDialogComponent> | null = null;

  public closeProcessingFileDialog$ = new Subject<void>();
  public supplierOrderCreated$ = new Subject<void>();
  /** מופעל כשהצעת מחיר נשלחה בהצלחה והדיאלוג נסגר – הצ'אט יכול לרענן */
  public priceQuoteSentSuccess$ = new Subject<void>();
  /** מופעל כש"התחלת ייצור" נשלח בהצלחה – צ'אט/printer/my-orders מרעננים */
  public productionStartedSuccess$ = new Subject<void>();
  /** מופעל כש"סימון שהיצור הסתיים" נשלח בהצלחה – printer/my-orders מרעננים רשימת הזמנות */
  public productionFinishedSuccess$ = new Subject<void>();
  /** מופעל כש"ההזמנה הגיעה לכתובת" נשלח בהצלחה – צ'אט/printer/my-orders מרעננים */
  public arrivedAtAddressSuccess$ = new Subject<void>();
  /** מופעל כש"זיכוי הזמנה" בוצע בהצלחה – printer/my-orders מרעננים */
  public creditOrderSuccess$ = new Subject<void>();
  public loginDialogClosed$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
  ) { }

  /** כיוון נוכחי לפתיחת דיאלוג – תואם ל־document.dir כדי שהדיאלוג ייפתח עם RTL/LTR נכון */
  private getDialogDirection(): 'ltr' | 'rtl' {
    return (document.documentElement.getAttribute('dir') || 'ltr') as 'ltr' | 'rtl';
  }

  // FOCUS ON TITLE
  focusMain() {
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active?.matches('input, textarea, select')) {
        return;
      }
      const dialogContainer = document.querySelector('mat-dialog-container');
      for (let i = 1; i <= 6; i++) {
        let heading = document.querySelector(`h${i}`);
        if (dialogContainer) {
          heading = dialogContainer.querySelector(`h${i}`);
        }
        if (heading && typeof (heading as HTMLElement).focus === 'function') {
          (heading as HTMLElement).setAttribute('tabindex', '-1');
          (heading as HTMLElement).focus();
          (heading as HTMLElement).classList.add('no-mobile-outline');
          break;
        }
      }
    }, 200);
  }

  /** תואם ל־AuthService.POST_LOGIN_SYSTEM_CHAT_KEY — ניקוי/הגדרה ב־onOpenLoginDialog */
  private static readonly POST_LOGIN_SYSTEM_CHAT_KEY = 'postLoginSystemChat';

  // Login Dialog
  onOpenLoginDialog(
    printingService: string = '',
    branch: string = '',
    systemChatAfterLogin?: '3DPrint' | 'phPrint',
  ): void {
    try {
      localStorage.removeItem(DialogService.POST_LOGIN_SYSTEM_CHAT_KEY);
    } catch {
      /* ignore */
    }
    if (systemChatAfterLogin === '3DPrint' || systemChatAfterLogin === 'phPrint') {
      try {
        localStorage.setItem(DialogService.POST_LOGIN_SYSTEM_CHAT_KEY, systemChatAfterLogin);
      } catch {
        /* ignore */
      }
    }
    if (printingService && printingService !== '' && branch && branch !== '') {
      localStorage.setItem("printingService", printingService);
      localStorage.setItem("branch", branch);
    }
    this.dialogLoginRef = this.dialog.open(PreloginComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-login-dialog',
    });
    this.dialogLoginRef.afterClosed().subscribe(() => {
      this.dialogLoginRef = null;
      this.loginDialogClosed$.next();
    });
    this.focusMain();
  }

  /**
   * Single-instance Error dialog: prevents duplicate alerts stacking.
   * If already open, does nothing (but re-focuses for accessibility).
   */
  onOpenErrorDialog(
    data: { message: string; branchName?: string | null; details?: string },
    options?: { panelClass?: string | string[] },
  ): void {
    if (this.dialogErrorRef) {
      this.focusMain();
      return;
    }
    const panelClass = options?.panelClass ?? 'zx-login-dialog';
    this.dialogErrorRef = this.dialog.open(ErrorComponent, {
      direction: this.getDialogDirection(),
      panelClass,
      data,
    });
    this.dialogErrorRef.afterClosed().subscribe(() => {
      this.dialogErrorRef = null;
    });
    this.focusMain();
  }

  onCloseLoginDialog(): void {
    if (this.dialogLoginRef) {
      this.dialogLoginRef.close();
      this.dialogLoginRef = null;
    }
    this.focusMain();
  }

  // Right Place Dialog
  onOpenRightPlaceDialog(): void {
    this.dialogRightPlaceRef = this.dialog.open(RightPlaceComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-rightplace-dialog',
    });
    this.focusMain();
  }

  onCloseRightPlaceDialog(): void {
    if (this.dialogRightPlaceRef) {
      this.dialogRightPlaceRef.close();
      this.dialogRightPlaceRef = null;
      this.focusMain();
    }
  }

  isRightPlaceDialogOpen(): boolean {
    return this.dialogRightPlaceRef !== null && this.dialogRightPlaceRef !== undefined;
  }

  // Image Preview Dialog
  onOpenImagePreviewDialog(
    printingService: string,
    currentFile: any,
    currentImage: any,
    realBranch: any,
    selectedPaper: any,
    cropRatioValue: boolean,
    isFromTable: boolean = true,
    orderStatus: string = '',
  ): void {
    this.dialogImagePreviewRef = this.dialog.open(ImagePreviewComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-image-preview-dialog',
      data: {
        printingService: printingService,
        currentFile: currentFile,
        currentImage: currentImage,
        realBranch: realBranch,
        selectedPaper: selectedPaper,
        cropRatioValue: cropRatioValue,
        isFromTable: isFromTable,
        orderStatus: orderStatus,
      }
    });
    this.focusMain();
  }

  onCloseImagePreviewDialog(): void {
    this.dialogImagePreviewRef.close();
    // Do not call focusMain() – it finds a heading in the page and focuses it, causing the orders list to scroll to top when closing via X button.
  }

  // Apply To All Dialog
  onOpenApplyToAllDialog(
    printingService: string,
    isOriginalPossible: boolean,
    printSettings: any,
    paperWidth: number,
    currentFile: any,
    currentImageIndex: number,
    paperType: string,
  ): void {
    this.dialogApplyToAllRef = this.dialog.open(ApplyToAllComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-login-dialog',
      data: {
        printingService: printingService,
        isOriginalPossible: isOriginalPossible,
        printSettings: printSettings,
        paperWidth: paperWidth,
        currentFile: currentFile,
        currentImageIndex: currentImageIndex,
        paperType: paperType,
      }
    });
    this.focusMain();
  }

  onCloseApplyToAllDialog(): void {
    this.dialogApplyToAllRef.close();
    this.focusMain();
  }

  // Resize Dialog
  onOpenResizeDialog(
    paperWidth: number,
    currentFile: any,
    currentImageIndex: number,
    paperType: string,
    currentImage: any,
    imagePath: string,
    isMustResize: boolean,
    serverAddress: string,
    paperId: string,
  ): void {
    this.dialogResizeRef = this.dialog.open(ResizeComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-resize-dialog',
      data: {
        paperWidth: paperWidth,
        currentFile: currentFile,
        currentImageIndex: currentImageIndex,
        paperType: paperType,
        currentImage: currentImage,
        imagePath: imagePath,
        isMustResize: isMustResize,
        serverAddress: serverAddress,
        paperId: paperId,
      }
    });
    this.focusMain();
  }

  onCloseResizeDialog(): void {
    this.dialogResizeRef.close();
    this.focusMain();
  }

  onCloseResizeDialogSuccess(): void {
    this.closeResizeDialogSource.next();
    this.dialogResizeRef.close();
    this.focusMain();
  }

  // Apply To All Dialog
  onOpenOrderSummaryDialog(
    printingService: string,
    serverAddress: string,
    branchPapers: any,
    files: any,
    branchName: string,
    totalPriceData: any,
    user: any,
    isPendingOrder: boolean = false,
    isAdminOrder: boolean = false,
    branchUnique: number = 0,
    branchID: string = '',
    printerID: string = '',
    orderID: string = '',
    printingCode: number = 0,
    fixProducts: any = null,
    isPicsPrinter: boolean = false,
    picsDiscount: number = null,
    collectionData: any = null,
    skipCode: boolean = false,
    product3D: any = null,
    phPaymentForOrder: { orderId: string; priceQuoteIndex: number; hasSavedCC?: boolean } | null = null,
    emptyPaymentForOrder: { fixProductsOrderId: string; expressOrderId?: string; totalCost: number; hasSavedCC?: boolean } | null = null,
    forceRasterize: boolean = false,
  ): void {
    const isIphone = /iPhone/.test(navigator.userAgent);
    let cdialogClass = 'zx-order-summary-dialog';
    if (isIphone) {
      cdialogClass = 'zx-order-summary-dialog-iphone';
    }
    this.dialogOrderSummaryRef = this.dialog.open(OrderSummaryComponent, {
      direction: this.getDialogDirection(),
      panelClass: cdialogClass,
      data: {
        printingService: printingService,
        serverAddress: serverAddress,
        branchPapers: branchPapers,
        files: files,
        branchName: branchName,
        totalPriceData: totalPriceData,
        user: user,
        isPendingOrder: isPendingOrder,
        isAdminOrder: isAdminOrder,
        branchUnique: branchUnique,
        branchID: branchID,
        printerID: printerID,
        orderID: orderID,
        printingCode: printingCode,
        fixProducts: fixProducts,
        isPicsPrinter: isPicsPrinter,
        is3DPrint: isPicsPrinter,
        picsDiscount: picsDiscount,
        collectionData: collectionData,
        skipCode: skipCode,
        product3D: product3D,
        phPaymentForOrder: phPaymentForOrder || null,
        emptyPaymentForOrder: emptyPaymentForOrder || null,
        forceRasterize: forceRasterize === true,
      }
    });
    this.focusMain();
  }

  onCloseOrderSummaryDialog(): void {
    this.dialogOrderSummaryRef.close();
    this.focusMain();
  }

  // delete order (orderType: 'ph' for PH orders with NOT_PAID – opens same dialog, calls PH cancel API)
  onOpenDeleteOrderDialog(
    order,
    isSu: boolean = false,
    orderType?: 'ph'
  ): void {
    this.dialogDeleteOrderRef = this.dialog.open(DeleteOrderComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        order: order,
        isSu: isSu,
        orderType: orderType ?? null,
      }
    });
    this.focusMain();
  }

  onCloseDeleteOrderDialog(): void {
    this.dialogDeleteOrderRef.close();
    this.focusMain();
  }

  // delete user
  onOpenDeleteUserDialog(
    user,
    isSU: boolean = false
  ): void {
    this.dialogDeleteUserRef = this.dialog.open(DeleteUserComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        user: user,
        isSU: isSU
      }
    });
    this.focusMain();
  }

  onCloseDeleteUserDialog(): void {
    this.dialogDeleteUserRef.close();
    this.focusMain();
  }

  // Add Points
  onOpenAddPointsDialog(
    user,
  ): void {
    // console.log("user from onOpenAddPointsDialog", user);
    this.dialogAddPointsRef = this.dialog.open(AddPointsComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        user: user,
      }
    });
    this.focusMain();
  }

  onCloseAddPointsDialog(): void {
    this.dialogAddPointsRef.close();
    this.focusMain();
  }

  // Edit Lock Code
  onOpenEditLockCodeDialog(printingService: string, printerId: string, currentLockCode: string = ''): void {
    this.dialogSuEditLockCodeRef = this.dialog.open(SuEditLockCodeComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        printingService: printingService,
        printerId: printerId,
        currentLockCode: currentLockCode
      }
    });
    this.focusMain();
  }

  onCloseEditLockCodeDialog(): void {
    if (this.dialogSuEditLockCodeRef) {
      this.dialogSuEditLockCodeRef.close();
    }
    this.focusMain();
  }

  // Close Branch
  onOpenCloseBranchDialog(service, branch, close_msg, isPicsPrinter = false, branchId: string = null): void {
    // Close previous dialog if it exists (when switching branches/printers)
    if (this.dialogCloseBranchRef) {
      try {
        this.dialogCloseBranchRef.close();
      } catch (error) {
        // Dialog may already be closed
      }
      this.dialogCloseBranchRef = null;
    }

    this.dialogCloseBranchRef = this.dialog.open(CloseBranchComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        service: service,
        branch: branch,
        close_msg: close_msg,
        isPicsPrinter: isPicsPrinter,
        branchId: branchId,
      }
    });

    // Reset dialogRef when dialog is closed (by any means: X button, click outside, etc.)
    this.dialogCloseBranchRef.afterClosed().subscribe(() => {
      this.dialogCloseBranchRef = null;
    });
    
    this.focusMain();
  }

  onCloseCloseBranchDialog(): void {
    if (this.dialogCloseBranchRef) {
      this.dialogCloseBranchRef.close();
      this.dialogCloseBranchRef = null;
    }
    this.focusMain();
  }

  informIfBranchCloseDialogIsOpened(): boolean {
    return !!this.dialogCloseBranchRef;
    this.focusMain();
  }

  // Phone Dialog — mandatory: after login, for an account without a phone, or whose unverified number
  // (sharedPhone) is also on another account; cannot be dismissed (verify a phone or log out)
  onOpenPhoneDialog(mandatory: boolean = false, sharedPhone: number | null = null): void {
    if (this.dialogPhoneRef) {
      this.focusMain();
      return;
    }
    this.dialogPhoneRef = this.dialog.open(PhoneComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-phone-dialog',
      disableClose: mandatory,
      data: { mandatory, sharedPhone },
    });
    this.dialogPhoneRef.afterClosed().subscribe(() => {
      this.dialogPhoneRef = null;
    });
    this.focusMain();
  }

  onClosePhoneDialog(): void {
    this.dialogPhoneRef?.close();
    this.focusMain();
  }

  // Scan Copy Dialog
  onOpenScanCopyDialog(expressBranch: any): void {
    this.dialogCopyScanRef = this.dialog.open(CopyScanComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-scan-copy-dialog',
      data: {
        expressBranch: expressBranch,
      }
    });
    this.focusMain();
  }

  onCloseScanCopyDialog(): void {
    this.dialogCopyScanRef.close();
    this.focusMain();
  }

  // Fix Products Dialog
  onOpenFixProductsDialog(branchName: string, products: any): void {
    this.dialogFixProductsRef = this.dialog.open(FixProductsComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-scan-copy-dialog',
      data: {
        branchName: branchName,
        products: products,
      }
    });
    this.focusMain();
  }

  onCloseFixProductsDialog(): void {
    this.dialogFixProductsRef.close();
    this.focusMain();
  }

  // Property Explain Dialog
  private explainPropertyResult$ = new Subject<string>();
  onOpenExplainPropertyDialog(
    property: string,
    printingService: string,
    realBranch: any,
    isPaperChoosen: boolean = false,
    isPh: boolean = false,
    isCollectOptions: boolean = false,
    materialData?: { availableMaterials: any[], selected3DMaterial: string, productName: string }
  ): void {
    this.resetExplainPropertyResult();
    this.dialogPropertyExplainRef = this.dialog.open(PropertyExplainComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-property-explain-dialog',
      data: {
        property: property,
        printingService: printingService,
        realBranch: realBranch,
        isPaperChoosen: isPaperChoosen,
        isPh: isPh,
        isCollectOptions: isCollectOptions,
        materialData: materialData
      }
    });
    this.focusMain();
  }

  onCloseExplainPropertyDialog(): void {
    this.dialogPropertyExplainRef.close();
    this.focusMain();
  }

  getExplainPropertyResult() {
    return this.explainPropertyResult$.asObservable();
    this.focusMain();
  }

  emitExplainPropertyResult(result: string) {
    this.explainPropertyResult$.next(result);
    this.focusMain();
  }

  resetExplainPropertyResult() {
    this.explainPropertyResult$.next('');
    this.focusMain();
  }

  // Border Explain Dialog
  onOpenBorderExplainDialog(): void {
    this.dialogBorderExplainRef = this.dialog.open(BorderExplainComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-property-explain-dialog',
    });
    this.focusMain();
  }

  onCloseBorderExplainDialog(): void {
    this.dialogBorderExplainRef.close();
    this.focusMain();
  }

  // Printer Number Dialog
  onOpenPrinterNumberDialog(): void {
    this.dialogPrinterNumberRef = this.dialog.open(PrinterNumberComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
    });
    this.focusMain();
  }

  onClosePrinterNumberDialog(): void {
    this.dialogPrinterNumberRef.close();
    this.focusMain();
  }


  // Su Close Branch Dialog
  onOpenSuCloseBranchDialog(
    isClose: boolean,
    service: string,
    branch: string,
    close_msg: string,
  ): void {
    this.dialogSuCloseBranchRef = this.dialog.open(SuCloseBranchComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        isClose: isClose,
        service: service,
        branch: branch,
        close_msg: close_msg,
      }
    });
    this.focusMain();
  }

  onCloseSuCloseBranchDialog(): void {
    this.dialogSuCloseBranchRef.close();
    this.focusMain();
  }

  // Su Edit User Dialog
  onOpenSuEditUserDialog(
    user: any,
  ): void {
    this.dialogSuEditUserRef = this.dialog.open(SuEditUserComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        user: user,
      }
    });
    this.focusMain();
  }

  onCloseSuEditUserDialog(): void {
    this.dialogSuEditUserRef.close();
    this.focusMain();
  }

  // Su Full Inventory Dialog
  onOpenSuFullInventoryDialog(): void {
    this.dialogSuFullInventoryRef = this.dialog.open(SuFullInventoryComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-image-preview-dialog',
    });
    this.focusMain();
  }

  onCloseSuFullInventoryDialog(): void {
    this.dialogSuFullInventoryRef.close();
    this.focusMain();
  }

  // Su New Order Supplier Dialog
  onOpenSuNewOrderSupplierDialog(branchData: any): void {
    this.dialogSuNewOrderSupplierRef = this.dialog.open(SuNewOrderSupplierComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-image-preview-dialog',
      data: branchData
    });
    this.focusMain();
  }

  onCloseSuNewOrderSupplierDialog(): void {
    this.dialogSuNewOrderSupplierRef.close();
    this.focusMain();
  }

  // Su All Screen Dialog
  onOpenSuAllScreensDialog(): void {
    this.dialogSuAllScreensRef = this.dialog.open(SuAllScreensComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-image-preview-dialog',
    });
    this.focusMain();
  }

  onClosSuAllScreensDialog(): void {
    this.dialogSuAllScreensRef.close();
    this.focusMain();
  }

  // Processing File Dialog
  onOpenProcessingFileDialog(
    service: string,
    file: any,
    isALotOfTime: boolean = true,
  ): void {
    if (!this.dialogProcessingFileRef) {
      this.dialogProcessingFileRef = this.dialog.open(ProcessingFileComponent, {
        direction: this.getDialogDirection(),
        panelClass: 'zx-image-preview-dialog',
        data: {
          service: service,
          file: file,
          isALotOfTime: isALotOfTime,
        }
      });
    }
    this.focusMain();
  }

  onCloseProcessingFileDialog(): void {
    this.closeProcessingFileDialog$.next();
    this.dialogProcessingFileRef.close();
    this.focusMain();
  }

  onOpenLanguageChangeDialog(
    usedLanguage: string,
    browserLanguage: string,
  ): void {
    if (!this.dialogLanguageChangeRef) {
      this.dialogLanguageChangeRef = this.dialog.open(LanguageChangeComponent, {
        direction: this.getDialogDirection(),
        panelClass: 'zx-language-chainge-dialog',
        hasBackdrop: false,
        data: {
          usedLanguage: usedLanguage,
          browserLanguage: browserLanguage,
        }
      });
    }
    this.focusMain();
  }

  onCloseLanguageChangeDialog(): void {
    this.dialogLanguageChangeRef.close();
    this.focusMain();
  }

  // Su Message To Customer Dialog
  onOpenSuMessageToCustomerDialog(branchData: any): void {
    this.dialogSuMessageToCustomerRef = this.dialog.open(SuMessageToCustomerComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-image-preview-dialog',
      data: branchData
    });
    this.focusMain();
  }

  onCloseSuMessageToCustomerDialog(): void {
    this.dialogSuMessageToCustomerRef.close();
    this.focusMain();
  }

  // Message To Customer View Dialog
  onOpenMessageToCustomerViewDialog(messageToCustomer: any, state: string = 'messageToCustomer', printingService?: string, branchOrPrinterId?: string): void {
    this.dialogMessageToCustomerViewRef = this.dialog.open(MessageToCustomerViewComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-phone-dialog',
      data: {
        messageToCustomer: messageToCustomer,
        state: state,
        printingService: printingService,
        branchOrPrinterId: branchOrPrinterId
      }
    });
    this.focusMain();
  }

  onCloseMessageToCustomerViewDialog(): void {
    this.dialogMessageToCustomerViewRef.close();
    this.focusMain();
  }

  // Transfer Files To User Dialog
  onOpenTransferFilesToUserDialog(files: any[], printingService: string, serverAddress: string = '', expressServerAddress: string = '', printerOrBranchID: string = ''): void {
    this.dialogTransferFilesToUserRef = this.dialog.open(TransferFilesToUserComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-login-dialog',
      data: {
        files: files,
        printingService: printingService,
        serverAddress: serverAddress,
        expressServerAddress: expressServerAddress,
        printerOrBranchID: printerOrBranchID
      }
    });
    this.focusMain();
  }

  onCloseTransferFilesToUserDialog(): void {
    if (this.dialogTransferFilesToUserRef) {
      this.dialogTransferFilesToUserRef.close();
    }
    this.focusMain();
  }

  /** PH “איך זה עובד” — `mode`: 3D product flow vs regular PH print flow */
  onOpenPhPrintExplanationDialog(options?: { mode?: PhPrintExplanationMode }): void {
    const mode: PhPrintExplanationMode = options?.mode ?? '3d';
    this.dialogPhPrintExplanationRef = this.dialog.open(PhPrintExplanationComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-ph-print-explanation-dialog',
      width: '90vw',
      maxWidth: '600px',
      maxHeight: '90vh',
      disableClose: false,
      autoFocus: false,
      data: { mode },
    });
    this.focusMain();
  }

  onClosePhPrintExplanationDialog(): void {
    if (this.dialogPhPrintExplanationRef) {
      this.dialogPhPrintExplanationRef.close();
    }
    this.focusMain();
  }

  // File Issues Dialog (handles file-format-not-supported and file-not-received-in-server)
  onOpenFileIssuesDialog(fileName: string, allowedFormats: string[], phoneNumber: string, state: string = 'file-format-not-supported', additionalData?: any): void {
    this.dialogFileIssuesRef = this.dialog.open(FileIssuesComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: {
        fileName: fileName,
        allowedFormats: allowedFormats,
        phoneNumber: phoneNumber,
        state: state,
        serverAddress: additionalData?.serverAddress || '',
        branchName: additionalData?.branchName || '',
        printingService: additionalData?.printingService || '',
        fileFormat: additionalData?.fileFormat || '',
        internetSpeed: additionalData?.internetSpeed || '',
        internetSpeedValue: additionalData?.internetSpeedValue || null,
        dpi: additionalData?.dpi || null,
        fileSizeMb: additionalData?.fileSizeMb ?? null,
        is3DPrint: additionalData?.is3DPrint || false
      }
    });
    this.focusMain();
  }

  onCloseFileIssuesDialog(): void {
    if (this.dialogFileIssuesRef) {
      this.dialogFileIssuesRef.close();
    }
    this.focusMain();
  }

  // Su Helpers List Dialog
  onOpenSuHelpersListDialog(): void {
    this.dialogSuHelpersListRef = this.dialog.open(SuHelpersListComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-image-preview-dialog',
    });
    this.focusMain();
  }

  onCloseSuHelpersListDialog(): void {
    this.dialogSuHelpersListRef.close();
    this.focusMain();
  }

  // Create Price Quote (צור הצעת מחיר)
  onOpenCreatePriceQuoteDialog(order: any): void {
    this.dialogCreatePriceQuoteRef = this.dialog.open(CreatePriceQuoteComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'create-price-quote-dialog'],
      data: { order, mode: 'price-quote' },
      maxHeight: '90vh',
      minHeight: '80vh',
    });
    this.dialogCreatePriceQuoteRef.afterClosed().subscribe(() => {
      this.dialogCreatePriceQuoteRef = null;
    });
    this.focusMain();
  }

  /** התחלת ייצור – פותח דיאלוג להזנת הערה אופציונלית (מבנה כמו סיום ייצור) */
  onOpenMarkProductionStartedDialog(order: any): void {
    this.dialogCreatePriceQuoteRef = this.dialog.open(CreatePriceQuoteComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'create-price-quote-dialog'],
      data: { order, mode: 'mark-production-started' },
      maxHeight: '90vh',
      minHeight: 'auto',
    });
    this.dialogCreatePriceQuoteRef.afterClosed().subscribe(() => {
      this.dialogCreatePriceQuoteRef = null;
    });
    this.focusMain();
  }

  /** סימון שהיצור הסתיים – פותח דיאלוג להזנת הודעה אופציונלית ללקוח */
  onOpenMarkProductionFinishedDialog(order: any): void {
    this.dialogCreatePriceQuoteRef = this.dialog.open(CreatePriceQuoteComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'create-price-quote-dialog'],
      data: { order, mode: 'mark-production-finished' },
      maxHeight: '90vh',
      minHeight: 'auto',
    });
    this.dialogCreatePriceQuoteRef.afterClosed().subscribe(() => {
      this.dialogCreatePriceQuoteRef = null;
    });
    this.focusMain();
  }

  /** סימון כהגעה לכתובת – פותח דיאלוג להזנת הודעה אופציונלית ללקוח */
  onOpenMarkArrivedAtAddressDialog(order: any): void {
    this.dialogCreatePriceQuoteRef = this.dialog.open(CreatePriceQuoteComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'create-price-quote-dialog'],
      data: { order, mode: 'mark-arrived-at-address' },
      maxHeight: '90vh',
      minHeight: 'auto',
    });
    this.dialogCreatePriceQuoteRef.afterClosed().subscribe(() => {
      this.dialogCreatePriceQuoteRef = null;
    });
    this.focusMain();
  }

  onCloseCreatePriceQuoteDialog(quoteSentSuccess?: boolean, fromMarkProductionStarted?: boolean, fromMarkProductionFinished?: boolean, fromMarkArrivedAtAddress?: boolean): void {
    if (this.dialogCreatePriceQuoteRef) {
      this.dialogCreatePriceQuoteRef.close();
      this.dialogCreatePriceQuoteRef = null;
      if (quoteSentSuccess) {
        this.priceQuoteSentSuccess$.next();
        if (fromMarkProductionStarted) {
          this.productionStartedSuccess$.next();
        }
        if (fromMarkProductionFinished) {
          this.productionFinishedSuccess$.next();
        }
        if (fromMarkArrivedAtAddress) {
          this.arrivedAtAddressSuccess$.next();
        }
      }
    }
    this.focusMain();
  }

  /** דיאלוג זיכוי הזמנה – mode: 'credit' | 'points', orderType: 'ph' | 'plotter' | 'express', service רק ל-plotter/express (מהדף printer). */
  onOpenCreditOrderDialog(
    order: any,
    mode: 'credit' | 'points',
    orderType: 'ph' | 'plotter' | 'express',
    service?: string
  ): void {
    const data: CreditOrderDialogData = { order, mode, orderType, service };
    this.dialogCreditOrderRef = this.dialog.open(CreditOrderComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data,
      maxHeight: '90vh',
    });
    this.dialogCreditOrderRef.afterClosed().subscribe((result) => {
      if (result) {
        this.creditOrderSuccess$.next();
      }
      this.dialogCreditOrderRef = null;
    });
    this.focusMain();
  }

  onCloseCreditOrderDialog(): void {
    if (this.dialogCreditOrderRef) {
      this.dialogCreditOrderRef.close();
      this.dialogCreditOrderRef = null;
    }
    this.focusMain();
  }

  onOpenResendPartialFilesDialog(
    order: any,
    orderType: 'plotter' | 'express',
    forceRasterize: boolean = false,
    expressPrintCode?: number,
    expressServerAddress?: string
  ): void {
    if (!order?._id || !order?.files?.length) {
      return;
    }
    const data: ResendPartialFilesDialogData = {
      order,
      orderType,
      forceRasterize: forceRasterize === true,
      expressPrintCode,
      expressServerAddress,
    };
    this.dialogResendPartialFilesRef = this.dialog.open(ResendPartialFilesDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-order-summary-dialog',
      data,
      maxHeight: '95vh',
    });
    this.dialogResendPartialFilesRef.afterClosed().subscribe(() => {
      this.dialogResendPartialFilesRef = null;
    });
    this.focusMain();
  }

  onCloseResendPartialFilesDialog(): void {
    if (this.dialogResendPartialFilesRef) {
      this.dialogResendPartialFilesRef.close();
      this.dialogResendPartialFilesRef = null;
    }
    this.focusMain();
  }

  onOpenInkGradientDialog(
    file: any,
    serverAddress: string,
    userNameForFile: string,
    order?: any
  ): void {
    this.dialog.open(InkGradientDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'zx-ink-gradient-dialog'],
      data: { file, serverAddress, userNameForFile, order },
      maxHeight: '90vh',
    });
    this.focusMain();
  }

  /** דיאלוג סטטוס PrintNode (תשובה משרת האקספרס) */
  onOpenPrintNodeStatusDialog(orderId: string, payload: unknown): void {
    const viewModel = parsePrintNodeStatusForDialog(payload);
    this.dialog.open(PrintNodeStatusDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'zx-printnode-status-dialog'],
      data: { orderId, viewModel },
      maxHeight: '90vh',
    });
    this.focusMain();
  }

  onOpenBillingErrorsDialog(order: any, transactions: BillingErrorsDialogData['transactions']): void {
    this.dialog.open(BillingErrorsDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'zx-billing-errors-dialog'],
      data: { mode: 'order', order, transactions },
      maxHeight: '90vh',
    });
    this.focusMain();
  }

  onOpenBillingErrorsForUserDialog(
    user: any,
    transactions: BillingErrorsDialogData['transactions']
  ): void {
    this.dialog.open(BillingErrorsDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'zx-billing-errors-dialog'],
      data: { mode: 'user', user, transactions },
      maxHeight: '90vh',
    });
    this.focusMain();
  }

  onOpenPaymentRequestDialog(user: any): void {
    this.dialogPaymentRequestRef = this.dialog.open(PaymentRequestDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: 'zx-printer-number-dialog',
      data: { user },
    });
    this.focusMain();
  }

  onClosePaymentRequestDialog(): void {
    this.dialogPaymentRequestRef?.close();
    this.focusMain();
  }

  onOpenWallContactDialog(data: WallContactDialogData): void {
    this.dialogWallContactRef?.close();
    const scrollState = this.captureWallScrollState();
    this.dialogWallContactRef = this.dialog.open(WallContactDialogComponent, {
      direction: this.getDialogDirection(),
      panelClass: ['zx-printer-number-dialog', 'zx-wall-contact-dialog'],
      data,
      autoFocus: false,
      restoreFocus: false,
    });
    this.dialogWallContactRef.afterClosed().subscribe(() => {
      this.restoreWallScrollState(scrollState);
      if (this.dialogWallContactRef) {
        this.dialogWallContactRef = null;
      }
    });
  }

  onCloseWallContactDialog(): void {
    this.dialogWallContactRef?.close();
    // Do not call focusMain() — it focuses a page heading and jumps the exhibition scroll.
  }

  /** Remember exhibition/wall scroller position so closing a wall dialog does not jump. */
  private captureWallScrollState(): { el: HTMLElement; top: number } | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const host = document.querySelector(
      '.eazix-exhibition, .personal-wall'
    ) as HTMLElement | null;
    if (!host) {
      return null;
    }
    const el = wallScrollParent(host);
    return { el, top: el.scrollTop };
  }

  private restoreWallScrollState(
    state: { el: HTMLElement; top: number } | null
  ): void {
    if (!state?.el) {
      return;
    }
    const apply = () => {
      state.el.scrollTop = state.top;
    };
    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
  }

  // =================
}

